/** Attendance filter type options — value sent to API; label shown in UI. */
export const ATTENDANCE_TYPE_FILTER_OPTIONS = [
  { label: 'Leave of Absence', value: 'loa' },
  { label: 'Medical Leave', value: 'medical_leave' },
  { label: 'Vacation', value: 'vacation' },
  { label: 'Remote Work', value: 'remote_work' },
  { label: 'Absent', value: 'absent' },
  { label: 'Late Arrival', value: 'late_arrival' },
  { label: 'Personal Leave', value: 'personal_leave' },
  { label: 'Extended Break', value: 'extended_break' },
  { label: 'Suspended', value: 'Suspended' }
];

export const TIME_OFF_TYPE_OPTIONS = [
  { label: 'Leave of Absence', value: 'loa' },
  { label: 'Medical Leave', value: 'medical_leave' },
  { label: 'Vacation', value: 'vacation' },
  { label: 'Remote Work', value: 'remote_work' },
  { label: 'Absent', value: 'absent' },
  { label: 'Late Arrival', value: 'late_arrival' },
  { label: 'Personal Leave', value: 'personal_leave' },
  { label: 'Extended Break', value: 'extended_break' }
];

/** Direct types dept_admin may create without eLeave (matches server allow-list). */
export const TIME_OFF_DEPT_ADMIN_DIRECT_TYPES = ['loa', 'absent', 'late_arrival'];

export const TIME_OFF_TYPE_LABELS = Object.fromEntries(
  TIME_OFF_TYPE_OPTIONS.map((o) => [o.value, o.label])
);

export function getDirectTimeOffTypeOptions(canCreateAllTypes) {
  if (canCreateAllTypes) return TIME_OFF_TYPE_OPTIONS;
  return TIME_OFF_TYPE_OPTIONS.filter((o) => TIME_OFF_DEPT_ADMIN_DIRECT_TYPES.includes(o.value));
}

export const ATTENDANCE_STATUS_OPTIONS = [
  { label: 'Active', value: 'Active' },
  { label: 'Rehired', value: 'Rehired' },
  { label: 'Suspended', value: 'Suspended' }
];

export const ATTENDANCE_COLUMN_TYPES = {
  loa: 'loa',
  medical: 'medical_leave',
  vacation: 'vacation',
  remote: 'remote_work'
};

export const TIME_OFF_TYPE_COLORS = {
  loa: '#a855f7',
  medical_leave: '#be185d',
  vacation: '#16a34a',
  personal_leave: '#16a34a',
  extended_break: '#0d9488',
  remote_work: '#3b82f6',
  absent: '#a855f7',
  late_arrival: '#a855f7'
};

export const TIME_OFF_OPEN_ENDED_END = '2099-12-31';

export function isOpenEndedTimeOffEnd(endDate) {
  const d = String(endDate || '').slice(0, 10);
  return d !== '' && d >= '2099-01-01';
}

/** Unplanned absences shown on the Daily Attendance Report. */
export const ATTENDANCE_MISSED_WARN_THRESHOLD = 3;

export const ATTENDANCE_MISSED_COLUMNS = [
  { key: 'missed_days', header: 'Missed', sortField: 'missed_days', primary: true, minWidth: '5.25rem' },
  { key: 'medical_days', header: 'Medical', sortField: 'medical_days', minWidth: '5.75rem' },
  { key: 'absent_days', header: 'Absent', sortField: 'absent_days', minWidth: '5.25rem' },
  { key: 'late_count', header: 'Late', sortField: 'late_count', integer: true, minWidth: '4.5rem' },
];

export const ATTENDANCE_MISSED_TABLE_COLUMNS = ATTENDANCE_MISSED_COLUMNS;

/** Planned leave totals for the report period (operational month / filter range). */
export const ATTENDANCE_LEAVE_COLUMNS = [
  { key: 'loa_days', header: 'LOA', sortField: 'loa_days', minWidth: '4.75rem' },
  { key: 'vacation_days', header: 'Vacation', sortField: 'vacation_days', minWidth: '5.5rem' },
  { key: 'personal_leave_days', header: 'Personal', sortField: 'personal_leave_days', minWidth: '5.5rem' },
  { key: 'extended_break_days', header: 'Extended', sortField: 'extended_break_days', minWidth: '5.75rem' },
  { key: 'remote_days', header: 'Remote', sortField: 'remote_days', minWidth: '5rem' },
];

/** YTD leave chips shown in the operational table Leave column. */
export const ATTENDANCE_LEAVE_YTD_ITEMS = [
  {
    key: 'ytd_loa_days',
    abbr: 'LOA',
    label: 'Leave of Absence',
    type: 'loa',
    sortField: 'ytd_loa_days',
    color: TIME_OFF_TYPE_COLORS.loa,
  },
  {
    key: 'ytd_vacation_days',
    abbr: 'Vac',
    label: 'Vacation',
    type: 'vacation',
    sortField: 'ytd_vacation_days',
    color: TIME_OFF_TYPE_COLORS.vacation,
    alwaysShow: true,
  },
  {
    key: 'ytd_personal_leave_days',
    abbr: 'Per',
    label: 'Personal leave',
    type: 'personal_leave',
    sortField: 'ytd_personal_leave_days',
    color: TIME_OFF_TYPE_COLORS.personal_leave,
    alwaysShow: true,
  },
  {
    key: 'ytd_extended_break_days',
    abbr: 'Ext',
    label: 'Extended Break',
    type: 'extended_break',
    sortField: 'ytd_extended_break_days',
    color: TIME_OFF_TYPE_COLORS.extended_break,
    alwaysShow: true,
  },
  {
    key: 'ytd_remote_days',
    abbr: 'Rem',
    label: 'Remote work',
    type: 'remote_work',
    sortField: 'ytd_remote_days',
    color: TIME_OFF_TYPE_COLORS.remote_work,
    alwaysShow: true,
  },
];

export const ATTENDANCE_METRIC_COLUMNS = [
  ...ATTENDANCE_MISSED_COLUMNS,
];

export const ATTENDANCE_TABLE_COLUMNS = [
  {
    key: 'loa',
    sortField: 'totals.loa',
    header: 'LOA',
    color: TIME_OFF_TYPE_COLORS.loa,
    type: ATTENDANCE_COLUMN_TYPES.loa
  },
  {
    key: 'medical',
    sortField: 'totals.medical',
    header: 'Medical',
    color: TIME_OFF_TYPE_COLORS.medical_leave,
    type: ATTENDANCE_COLUMN_TYPES.medical
  },
  {
    key: 'vacation',
    sortField: 'totals.vacation',
    header: 'Vacation',
    color: TIME_OFF_TYPE_COLORS.vacation,
    type: ATTENDANCE_COLUMN_TYPES.vacation
  },
  {
    key: 'remote',
    sortField: 'totals.remote',
    header: 'Remote',
    color: TIME_OFF_TYPE_COLORS.remote_work,
    type: ATTENDANCE_COLUMN_TYPES.remote
  }
];

/** Map legacy attendance table sort keys saved before totals.* paths. */
export const ATTENDANCE_SORT_FIELD_ALIASES = Object.fromEntries(
  ATTENDANCE_TABLE_COLUMNS.map((col) => [col.key, col.sortField])
);

/** Mock default until allowance fields exist on employees (Phase 5+). */
export const DEFAULT_VACATION_ALLOWANCE = 28;

export function getTimeOffTypeColor(type) {
  return TIME_OFF_TYPE_COLORS[type] || '#a855f7';
}

export function getTimeOffTypeLabel(type) {
  return TIME_OFF_TYPE_LABELS[type] || type;
}

/** Leave types for the "Leave" history filter (excludes late_arrival → issues). */
export const LEAVE_ENTRY_TYPES = [
  'loa',
  'medical_leave',
  'vacation',
  'personal_leave',
  'extended_break',
  'absent'
];

/** Types shown under the "Attendance issues" history filter. */
export const ATTENDANCE_ISSUE_TYPES = ['late_arrival', 'absent'];

/** Scheduled absences for the status-strip "next absence" widget (excludes late). */
export const SCHEDULED_ABSENCE_TYPES = [
  'vacation',
  'loa',
  'medical_leave',
  'personal_leave',
  'extended_break',
  'absent'
];

export function yearDateRange(year) {
  const y = Number(year) || new Date().getFullYear();
  return {
    date_from: `${y}-01-01`,
    date_to: `${y}-12-31`
  };
}

export function currentYearDateRange() {
  return yearDateRange(new Date().getFullYear());
}

export function currentMonthDateRange() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const monthPad = String(month + 1).padStart(2, '0');
  const lastDay = new Date(year, month + 1, 0).getDate();
  const dayPad = String(lastDay).padStart(2, '0');
  return {
    date_from: `${year}-${monthPad}-01`,
    date_to: `${year}-${monthPad}-${dayPad}`,
  };
}

export function formatAttendanceDays(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '0';
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

export { toApiDate } from '@/utils/dateHelper';

export function isValidDateRange(dateFrom, dateTo) {
  if (!dateFrom || !dateTo) return true;
  return dateFrom <= dateTo;
}

/** Map attendance/get summary to workspace header StatCards. */
export function mapAttendanceHeaderStats(summary) {
  const s = summary || {};
  return [
    { key: 'loa', label: 'LOA', value: formatAttendanceDays(s.loa?.days), tone: 'loa' },
    { key: 'vacation', label: 'Vacation', value: formatAttendanceDays(s.vacation?.days), tone: 'vacation' },
    { key: 'extended', label: 'Extended Break', value: formatAttendanceDays(s.extended?.days), tone: 'extended' },
    { key: 'remote', label: 'Remote', value: formatAttendanceDays(s.remote?.days), tone: 'remote' },
    { key: 'medical', label: 'Medical', value: formatAttendanceDays(s.medical?.days), tone: 'medical' },
    { key: 'absent', label: 'Absent', value: formatAttendanceDays(s.absent?.days), tone: 'absent' },
    { key: 'late', label: 'Late', value: String(s.late?.employees ?? 0), tone: 'late' }
  ];
}
